import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  ContextService,
  FavoriteListService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import { Octopus, Session } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  CreateSyncConfigurationRequest,
  MicrosoftTokenBundle,
  OUTLOOK_CATEGORY,
  OutlookSyncOAuthFlowService,
  OutlookSyncOAuthFlowState,
  OutlookSyncTokenType,
  SyncConfigurationFacade,
  UpdateSyncConfiguration,
  UserSyncConfiguration,
  computePatExpiration,
} from '@maximizer/outlook-sync/data-access';
import {
  TrackerService,
  secureRandomId,
} from '@maximizer/outlook/shared/data-access';
import { PersonalAccessTokenService } from '@maximizer/core/personal-access-token/data-access';
import { PopupSessionExpiredService } from '@maximizer/oauth2/data-access';
import { firstValueFrom } from 'rxjs';
import {
  InstallAttempt,
  InstallEntrySource,
  InstallResumeResult,
  InstallStartResult,
  InstallStep,
} from './install.model';

const HANDSHAKE_COMPLETE_MESSAGE_TYPE = 'mx-sync-config-handshake-complete';
const TRACKER_MODULE = 'sync-configuration';

type InstallCredentials = Required<
  Pick<
    CreateSyncConfigurationRequest,
    'microsoftUserId' | 'microsoftUserEmail' | 'maximizerAuth' | 'microsoftAuth'
  >
>;

type FavoriteListRef = { key: string; name: string };

@Injectable({ providedIn: 'root' })
export class SyncConfigurationInstallOrchestrator {
  private readonly outlookSync = inject(SyncConfigurationFacade);
  private readonly patService = inject(PersonalAccessTokenService);
  private readonly oauthFlow = inject(OutlookSyncOAuthFlowService);
  private readonly insights = inject(InsightsService);
  private readonly router = inject(Router);
  private readonly session = inject(SessionService);
  private readonly context = inject(ContextService);
  private readonly favoriteListService = inject(FavoriteListService);
  private readonly popupSessionSignal = inject(PopupSessionExpiredService);
  private readonly sessionExpired = this.popupSessionSignal.expired;
  private readonly tracker = inject(TrackerService);

  readonly attempt = signal<InstallAttempt | null>(null);
  readonly failure = computed(() => this.attempt()?.failure ?? null);

  private attemptStartedAtMs = 0;
  private pendingCorrelationId: string | null = null;

  /** Session values are typed `string` but come from untyped JSON, so coerce before validating. */
  private asTrimmed(value: string | null | undefined): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  async start(
    options: {
      entrySource?: InstallEntrySource;
      forceInstall?: boolean;
    } = {},
  ): Promise<InstallStartResult> {
    const entrySource = options.entrySource ?? 'cta';
    const forceInstall = options.forceInstall ?? false;
    this.reset();

    const correlationId = secureRandomId();
    this.pendingCorrelationId = correlationId;

    let sessionInfo: Session | undefined;
    try {
      sessionInfo = await firstValueFrom(this.session.getInfo());
    } catch (error) {
      this.recordFailure('session-unavailable', error);
      return this.failureResult();
    }

    const userUid = this.asTrimmed(sessionInfo?.user?.id);
    const workspaceId = this.asTrimmed(sessionInfo?.workspace);
    const userEmail = this.asTrimmed(sessionInfo?.user?.email1?.email);
    const tenantAlias = this.asTrimmed(sessionInfo?.alias);
    const tenantId = this.asTrimmed(this.context.tenantId);

    this.attempt.set({
      correlationId,
      maximizerUserUid: userUid,
      maximizerUserEmail: userEmail,
      workspaceId,
      step: 'session-validation',
      entrySource,
    });

    const missingSessionFields = Object.entries({
      'user id': userUid,
      workspace: workspaceId,
      alias: tenantAlias,
      'tenant id': tenantId,
    })
      .filter(([, value]) => !value)
      .map(([name]) => name);
    if (missingSessionFields.length > 0) {
      this.recordFailure(
        'session-validation',
        new Error(
          `Session missing required field(s): ${missingSessionFields.join(', ')}.`,
        ),
        { missingFields: missingSessionFields.join(',') },
      );
      return this.failureResult();
    }

    this.advance('idempotency-precheck');

    // Precheck runs even when forceInstall=true so soft-disabled configs get re-enabled.
    let existing: UserSyncConfiguration | undefined;
    let reinstallConfigurationId = '';
    try {
      existing = await firstValueFrom(
        this.outlookSync.getConfigurationForInstallPrecheck(
          userUid,
          workspaceId,
        ),
      );
    } catch (error) {
      this.recordFailure('idempotency-precheck', error);
      return this.failureResult();
    }
    if (existing) {
      if (existing.enabled === false) {
        const reEnabled = await this.reEnableExistingConfiguration(
          existing,
          userUid,
        );
        if (!reEnabled) {
          return this.failureResult();
        }
        this.advance('success');
        return { type: 'existing', record: reEnabled };
      }
      if (!forceInstall) {
        this.advance('success');
        return { type: 'existing', record: existing };
      }
      // forceInstall=true + existing+enabled → fall through to OAuth.
      reinstallConfigurationId = this.asTrimmed(existing.id);
    }

    const installUserEmail =
      userEmail || this.asTrimmed(existing?.maximizerUserEmail);
    if (!installUserEmail) {
      this.recordFailure(
        'profile-email-missing',
        new Error(
          'Neither the Maximizer user profile nor the existing sync configuration has an email address.',
        ),
        { missingFields: 'email' },
      );
      return this.failureResult();
    }

    this.advance('microsoft-oauth-redirect');
    this.trackSyncConfigurationEvent('install', 'started', {
      entrySource,
      forceInstall,
    });
    try {
      await this.oauthFlow.startInstallFlow(
        userUid,
        installUserEmail,
        workspaceId,
        correlationId,
        tenantAlias,
        tenantId,
        reinstallConfigurationId,
      );
      return { type: 'redirected' };
    } catch (error) {
      this.recordFailure('microsoft-oauth-redirect', error);
      return this.failureResult();
    }
  }

  async resume(
    options: {
      navigateOnSuccess?: boolean;
      entrySource?: InstallEntrySource;
    } = {},
  ): Promise<InstallResumeResult> {
    const navigateOnSuccess = options.navigateOnSuccess ?? true;
    const entrySource = options.entrySource ?? 'callback';
    this.reset();

    const ready = await this.readInstallCallback(entrySource);
    if (!ready) return this.failureResult();

    const { code, state } = ready;
    if (!(await this.verifyCallbackSession(state))) {
      return this.failureResult();
    }

    const precheck = await this.resolveReinstallTarget(state, navigateOnSuccess);
    if (precheck.outcome === 'done') return precheck.result;
    const reinstallTarget = precheck.reinstallTarget;

    if (!this.asTrimmed(state.maximizerUserEmail)) {
      this.recordFailure(
        'profile-email-missing',
        new Error('OAuth flow state carries no Maximizer email address.'),
        { missingFields: 'email' },
      );
      return this.failureResult();
    }

    const reinstallFavoriteKey = this.asTrimmed(
      reinstallTarget?.contactsSync?.favoriteListKey,
    );
    const favoriteList = await this.resolveResumeFavoriteList(
      state,
      reinstallTarget,
      reinstallFavoriteKey,
    );
    if (!favoriteList) return this.failureResult();

    const acquired = await this.acquireInstallCredentials(code, state);
    if (!acquired) return this.failureResult();

    const userSyncConfigurationId = await this.persistConfiguration(
      state,
      reinstallTarget,
      favoriteList,
      acquired,
      reinstallFavoriteKey,
    );
    if (!userSyncConfigurationId) return this.failureResult();

    this.attempt.update((current) =>
      current
        ? { ...current, userSyncConfigurationId, step: 'success' }
        : current,
    );
    this.emitSuccessTelemetry(userSyncConfigurationId);
    void this.emitPostSuccessHealthTelemetry(userSyncConfigurationId);
    this.navigateAndSignal(navigateOnSuccess);
    return { type: 'success' };
  }

  private async readInstallCallback(
    entrySource: InstallEntrySource,
  ): Promise<{ code: string; state: OutlookSyncOAuthFlowState } | null> {
    const correlationIdFromParams = this.oauthFlow.peekCorrelationId();

    await this.oauthFlow.resumeFromUrl();

    const ready = this.oauthFlow.installCodeReady();
    this.oauthFlow.installCodeReady.set(null);

    const oauthError = this.oauthFlow.errorMessage();

    if (!ready) {
      this.attempt.set({
        correlationId: correlationIdFromParams ?? 'unknown',
        maximizerUserUid: '',
        maximizerUserEmail: '',
        workspaceId: '',
        step: 'microsoft-oauth-callback',
        entrySource,
      });

      const step = this.classifyCallbackFailure(
        this.oauthFlow.errorCode(),
        oauthError,
      );
      this.recordFailure(
        step,
        new Error(oauthError ?? 'Microsoft OAuth callback failed.'),
      );
      return null;
    }

    this.attempt.set({
      correlationId: ready.state.correlationId ?? 'unknown',
      maximizerUserUid: ready.state.maximizerUserUid,
      maximizerUserEmail: ready.state.maximizerUserEmail ?? '',
      workspaceId: ready.state.workspaceId ?? '',
      step: 'user-sync-configuration-create',
      entrySource,
    });
    return ready;
  }

  private async resolveReinstallTarget(
    state: OutlookSyncOAuthFlowState,
    navigateOnSuccess: boolean,
  ): Promise<
    | { outcome: 'continue'; reinstallTarget: UserSyncConfiguration | undefined }
    | { outcome: 'done'; result: InstallResumeResult }
  > {
    const reinstallConfigurationId =
      state.mode === 'reinstall' ? this.asTrimmed(state.configurationId) : '';

    let raceCheck: UserSyncConfiguration | undefined;
    try {
      raceCheck = await firstValueFrom(
        this.outlookSync.getConfigurationForInstallPrecheck(
          state.maximizerUserUid,
          state.workspaceId ?? '',
        ),
      );
    } catch (error) {
      this.recordFailure('idempotency-precheck', error);
      return { outcome: 'done', result: this.failureResult() };
    }

    const reinstallTarget =
      !!reinstallConfigurationId &&
      this.asTrimmed(raceCheck?.id) === reinstallConfigurationId
        ? raceCheck
        : undefined;

    if (raceCheck && !reinstallTarget) {
      return {
        outcome: 'done',
        result: await this.handleRaceCheck(
          raceCheck,
          state.maximizerUserUid,
          navigateOnSuccess,
        ),
      };
    }
    if (!raceCheck && reinstallConfigurationId) {
      this.recordFailure(
        'user-sync-configuration-update',
        new Error('Reinstall target configuration no longer exists.'),
      );
      return { outcome: 'done', result: this.failureResult() };
    }

    return { outcome: 'continue', reinstallTarget };
  }

  private async resolveResumeFavoriteList(
    state: OutlookSyncOAuthFlowState,
    reinstallTarget: UserSyncConfiguration | undefined,
    reinstallFavoriteKey: string,
  ): Promise<FavoriteListRef | null> {
    if (reinstallFavoriteKey) {
      return {
        key: reinstallFavoriteKey,
        name:
          this.asTrimmed(reinstallTarget?.contactsSync?.favoriteListName) ||
          this.favoriteListNameFor(state.maximizerUserUid),
      };
    }

    this.advance('favorite-list-create');
    try {
      return await this.resolveInstallFavoriteList(state.maximizerUserUid);
    } catch (error) {
      this.recordFailure('favorite-list-create', error);
      return null;
    }
  }

  private async acquireInstallCredentials(
    code: string,
    state: OutlookSyncOAuthFlowState,
  ): Promise<{ credentials: InstallCredentials; patKey: string } | null> {
    this.advance('microsoft-token-exchange');
    let msTokens: MicrosoftTokenBundle;
    try {
      msTokens = await this.oauthFlow.exchangeInstallCode(code);
    } catch (error) {
      this.recordFailure('microsoft-token-exchange', error);
      return null;
    }

    this.advance('maximizer-pat');
    let patKey: string | null;
    const patExpiration = computePatExpiration();
    try {
      patKey = await firstValueFrom(
        this.patService.create(
          'outlook-sync',
          patExpiration.isoDate,
          state.maximizerUserUid,
        ),
      );
    } catch (error) {
      this.recordFailure('maximizer-pat', error);
      return null;
    }
    if (!patKey) {
      this.recordFailure(
        'maximizer-pat',
        new Error('Maximizer PAT endpoint returned a null/empty token.'),
      );
      return null;
    }

    const auth = msTokens.microsoftAuth;
    return {
      patKey,
      credentials: {
        microsoftUserId: msTokens.microsoftUserId,
        microsoftUserEmail: msTokens.microsoftUserEmail,
        maximizerAuth: {
          tokenType: OutlookSyncTokenType.PersonalAccessToken,
          personalAccessToken: patKey,
          expiresIn: patExpiration.expiresInSeconds.toString(),
        },
        microsoftAuth: {
          tokenType: OutlookSyncTokenType.RefreshToken,
          scopes: auth.scopes,
          refreshToken: auth.refreshToken,
          expiresIn: auth.expiresIn,
        },
      },
    };
  }

  private async persistConfiguration(
    state: OutlookSyncOAuthFlowState,
    reinstallTarget: UserSyncConfiguration | undefined,
    favoriteList: FavoriteListRef,
    acquired: { credentials: InstallCredentials; patKey: string },
    reinstallFavoriteKey: string,
  ): Promise<string | null> {
    const persistStep: InstallStep = reinstallTarget
      ? 'user-sync-configuration-update'
      : 'user-sync-configuration-create';
    this.advance(persistStep);

    try {
      return reinstallTarget
        ? await this.updateForReinstall(
            state,
            reinstallTarget,
            favoriteList,
            acquired.credentials,
            reinstallFavoriteKey,
          )
        : await this.createForInstall(
            state,
            favoriteList,
            acquired.credentials,
          );
    } catch (error) {
      if (!reinstallTarget || this.isRejectedBeforeCommit(error)) {
        await this.revokeOrphanPat(acquired.patKey);
      }
      this.recordFailure(persistStep, error);
      return null;
    }
  }

  private async updateForReinstall(
    state: OutlookSyncOAuthFlowState,
    reinstallTarget: UserSyncConfiguration,
    favoriteList: FavoriteListRef,
    credentials: InstallCredentials,
    reinstallFavoriteKey: string,
  ): Promise<string> {
    const payload: UpdateSyncConfiguration = {
      maximizerUserUid: state.maximizerUserUid,
      maximizerUserEmail: state.maximizerUserEmail,
      ...(reinstallTarget.emailsSync
        ? { emailsSync: reinstallTarget.emailsSync }
        : {}),
      ...(reinstallFavoriteKey || !reinstallTarget.contactsSync
        ? {}
        : {
            contactsSync: {
              ...reinstallTarget.contactsSync,
              favoriteListKey: favoriteList.key,
              favoriteListName: favoriteList.name,
            },
          }),
      ...credentials,
    };
    const ok = await firstValueFrom(
      this.outlookSync.updateConfiguration(payload, reinstallTarget.id),
    );
    if (!ok) {
      throw new Error(
        'updateConfiguration returned a non-OK status for reinstall.',
      );
    }
    return reinstallTarget.id;
  }

  private async createForInstall(
    state: OutlookSyncOAuthFlowState,
    favoriteList: FavoriteListRef,
    credentials: InstallCredentials,
  ): Promise<string> {
    const request: CreateSyncConfigurationRequest = {
      maximizerUserUid: state.maximizerUserUid,
      maximizerUserEmail: state.maximizerUserEmail,
      tenantAlias: state.tenantAlias,
      tenantId: state.tenantId,
      workspaceId: state.workspaceId,
      contactsSync: {
        enabled: false,
        favoriteListKey: favoriteList.key,
        favoriteListName: favoriteList.name,
        outlookCategory: OUTLOOK_CATEGORY,
      },
      appointmentsSync: { enabled: false },
      tasksSync: { enabled: false },
      emailsSync: { enabled: false, syncEverything: true },
      ...credentials,
    };
    const id = await firstValueFrom(
      this.outlookSync.createConfiguration(request),
    );
    if (!id) {
      throw new Error('createConfiguration returned empty id.');
    }
    return id;
  }

  private async handleRaceCheck(
    raceCheck: UserSyncConfiguration,
    maximizerUserUid: string,
    navigateOnSuccess: boolean,
  ): Promise<InstallResumeResult> {
    if (raceCheck.enabled === false) {
      const reEnabled = await this.reEnableExistingConfiguration(
        raceCheck,
        maximizerUserUid,
      );
      if (!reEnabled) {
        return this.failureResult();
      }
      const reEnabledId = reEnabled.id ?? '';
      this.attempt.update((current) =>
        current
          ? { ...current, userSyncConfigurationId: reEnabledId, step: 'success' }
          : current,
      );
      this.emitSuccessTelemetry(reEnabledId);
      this.navigateAndSignal(navigateOnSuccess);
      return { type: 'success' };
    }
    this.handleInstallSuperseded(raceCheck.id ?? '');
    this.navigateAndSignal(navigateOnSuccess);
    return { type: 'superseded' };
  }

  private navigateAndSignal(navigateOnSuccess: boolean): void {
    if (navigateOnSuccess) {
      this.router.navigateByUrl('/sync-configuration');
    }
    this.signalOpenerComplete();
  }

  private async reEnableExistingConfiguration(
    existing: UserSyncConfiguration,
    userUid: string,
  ): Promise<UserSyncConfiguration | null> {
    const id = existing.id;
    if (!id) {
      this.recordFailure(
        'user-sync-configuration-re-enable',
        new Error('Existing sync configuration is missing an id.'),
      );
      return null;
    }

    // The key is restored whenever it is missing, not just when contacts sync is on, so a later toggle-on is not rejected by the API.
    let contactsSync = existing.contactsSync;
    if (contactsSync && !this.asTrimmed(contactsSync.favoriteListKey)) {
      this.advance('favorite-list-create');
      try {
        const favoriteList = await this.resolveInstallFavoriteList(userUid);
        contactsSync = {
          ...contactsSync,
          favoriteListKey: favoriteList.key,
          favoriteListName: favoriteList.name,
        };
      } catch (error) {
        this.recordFailure('favorite-list-create', error);
        return null;
      }
    }

    this.advance('user-sync-configuration-re-enable');

    const payload: UpdateSyncConfiguration = {
      maximizerUserUid: existing.maximizerUserUid ?? userUid,
      microsoftUserEmail: existing.microsoftUserEmail,
      microsoftUserId: existing.microsoftUserId,
      contactsSync,
      tasksSync: existing.tasksSync,
      appointmentsSync: existing.appointmentsSync,
      emailsSync: existing.emailsSync,
    };

    try {
      const ok = await firstValueFrom(
        this.outlookSync.updateConfiguration(payload, id),
      );
      if (!ok) {
        this.recordFailure(
          'user-sync-configuration-re-enable',
          new Error(
            `updateConfiguration returned non-OK status for re-enable (id=${id})`,
          ),
        );
        return null;
      }
    } catch (error) {
      this.recordFailure('user-sync-configuration-re-enable', error);
      return null;
    }

    this.trackSyncConfigurationEvent('re-enable', 'completed', {
      userSyncConfigurationId: id,
    });

    return {
      ...existing,
      contactsSync,
      enabled: true,
      needsManualIntervention: false,
    };
  }

  private async verifyCallbackSession(
    state: OutlookSyncOAuthFlowState,
  ): Promise<boolean> {
    let sessionInfo: Session | undefined;
    try {
      sessionInfo = await firstValueFrom(this.session.getInfo());
    } catch (error) {
      this.recordFailure('session-unavailable', error);
      return false;
    }

    // getInfo() can resolve with an unpopulated session; without this guard every field mismatches and it reads as an identity change.
    if (!this.asTrimmed(sessionInfo?.user?.id)) {
      this.recordFailure(
        'session-unavailable',
        new Error('Session resolved without a user on the OAuth callback.'),
      );
      return false;
    }

    const sameValue = (
      fromState: string | null | undefined,
      fromSession: string | null | undefined,
    ): boolean =>
      this.asTrimmed(fromState).toLowerCase() ===
      this.asTrimmed(fromSession).toLowerCase();

    const mismatched = Object.entries({
      'user id': sameValue(state.maximizerUserUid, sessionInfo?.user?.id),
      workspace: sameValue(state.workspaceId, sessionInfo?.workspace),
      alias: sameValue(state.tenantAlias, sessionInfo?.alias),
      'tenant id': sameValue(state.tenantId, this.context.tenantId),
    })
      .filter(([, matches]) => !matches)
      .map(([name]) => name);

    if (mismatched.length > 0) {
      this.recordFailure(
        'session-identity-mismatch',
        new Error(
          `Callback state belongs to another session; mismatched field(s): ${mismatched.join(', ')}.`,
        ),
        { mismatchedFields: mismatched.join(',') },
      );
      return false;
    }

    return true;
  }

  /** The PAT outlives the failed attempt otherwise; the original error stays the reported one. */
  private async revokeOrphanPat(patKey: string): Promise<void> {
    const revoked = await firstValueFrom(this.patService.revoke(patKey))
      .then(() => true)
      .catch(() => false);
    const attempt = this.attempt();
    this.insights.trackEvent({
      name: 'sync-configuration-install.orphan-pat-cleanup',
      properties: {
        feature: 'sync-configuration-install',
        correlationId: attempt?.correlationId ?? 'unknown',
        maximizerUserUid: attempt?.maximizerUserUid ?? '',
        revoked: String(revoked),
      },
    });
  }

  /** Creator is typed `string` but Octopus returns it as an entity object in practice, so a strict === would never match and every install would create a duplicate list. */
  private isCreatedBy(creator: unknown, maximizerUserUid: string): boolean {
    const uid = maximizerUserUid.trim().toLowerCase();
    if (!uid) return false;
    if (typeof creator === 'string') return creator.trim().toLowerCase() === uid;
    if (creator && typeof creator === 'object') {
      const record = creator as Record<string, unknown>;
      const identifiers = [record['Uid'], record['Key']].filter(
        (value) => typeof value === 'string',
      );
      const candidates =
        identifiers.length > 0 ? identifiers : Object.values(record);
      return candidates.some(
        (value) => typeof value === 'string' && value.trim().toLowerCase() === uid,
      );
    }
    return false;
  }

  private favoriteListNameFor(maximizerUserUid: string): string {
    return `Outlook Sync (${maximizerUserUid})`;
  }

  private async resolveInstallFavoriteList(
    maximizerUserUid: string,
  ): Promise<{ key: string; name: string }> {
    const expectedName = this.favoriteListNameFor(maximizerUserUid);
    const lists = await firstValueFrom(
      this.favoriteListService.getAddressBookFavoriteList(),
    );
    const existing = lists.find(
      (list) =>
        !!list.key &&
        list.name?.trim().toLowerCase() === expectedName.toLowerCase() &&
        this.isCreatedBy(list.creator, maximizerUserUid),
    );
    if (existing?.key) {
      return {
        key: existing.key,
        name: existing.name?.trim() || expectedName,
      };
    }

    const created = await firstValueFrom(
      this.favoriteListService.createAddressBookFavoriteList(
        expectedName,
        Octopus.EntityType.AbEntry,
      ),
    );
    if (!created?.key) {
      throw new Error('Favorite list endpoint returned a null/empty key.');
    }
    return {
      key: created.key,
      name: created.name?.trim() || expectedName,
    };
  }

  private handleInstallSuperseded(existingRecordId: string): void {
    const attempt = this.attempt();
    this.insights.trackEvent({
      name: 'sync-configuration-install.install-superseded',
      properties: {
        feature: 'sync-configuration-install',
        step: 'install-superseded',
        maximizerUserUid: attempt?.maximizerUserUid ?? '',
        correlationId: attempt?.correlationId ?? 'unknown',
        userSyncConfigurationId: existingRecordId,
        timestampUtc: new Date().toISOString(),
      },
    });
    this.attempt.update((current) =>
      current ? { ...current, step: 'install-superseded' } : current,
    );
  }

  private emitSuccessTelemetry(userSyncConfigurationId: string): void {
    const attempt = this.attempt();
    if (!attempt) return;
    const elapsedMs = performance.now() - this.attemptStartedAtMs;
    this.insights.trackEvent({
      name: 'sync-configuration-install.completed',
      properties: {
        feature: 'sync-configuration-install',
        correlationId: attempt.correlationId,
        maximizerUserUid: attempt.maximizerUserUid,
        userSyncConfigurationId,
        elapsedMs: String(elapsedMs),
        installEntrySource: attempt.entrySource ?? 'callback',
      },
    });
    this.trackSyncConfigurationEvent('install', 'completed', {
      result: 'success',
      userSyncConfigurationId,
      elapsedMs,
      entrySource: attempt.entrySource ?? 'callback',
    });
  }

  private async emitPostSuccessHealthTelemetry(
    userSyncConfigurationId: string,
  ): Promise<void> {
    const attempt = this.attempt();
    if (!attempt) return;
    try {
      const health = await firstValueFrom(
        this.outlookSync.getHealthStatus(userSyncConfigurationId),
      );
      if (!health) return;
      if (!health.isMicrosoftAuthHealthy || !health.isMaximizerAuthHealthy) {
        this.insights.trackEvent({
          name: 'sync-configuration-install.post-success-unhealthy',
          properties: {
            feature: 'sync-configuration-install',
            correlationId: attempt.correlationId,
            maximizerUserUid: attempt.maximizerUserUid,
            userSyncConfigurationId,
            isMicrosoftAuthHealthy: String(health.isMicrosoftAuthHealthy),
            isMaximizerAuthHealthy: String(health.isMaximizerAuthHealthy),
          },
        });
      }
    } catch {
      // best-effort
    }
  }

  private reset(): void {
    this.attempt.set(null);
    this.pendingCorrelationId = null;
    this.attemptStartedAtMs = performance.now();
  }

  private recordFailure(
    step: InstallStep,
    error: unknown,
    extraProps?: Record<string, string>,
  ): void {
    if (this.attempt()?.failure) return;

    const httpStatus =
      error instanceof HttpErrorResponse ? error.status : undefined;
    const isAuthFailure =
      this.sessionExpired() !== null &&
      (httpStatus === HttpStatusCode.Unauthorized ||
        httpStatus === HttpStatusCode.Forbidden);

    if (isAuthFailure) {
      this.attempt.update((current) =>
        current ? { ...current, step } : current,
      );
      return;
    }

    const errorMessage = this.describeError(error);
    const dialogBodyKey =
      error instanceof HttpErrorResponse &&
      this.hasMicrosoftEmailConflict(error)
        ? 'outlookSync.install.duplicateAccountDialogBody'
        : undefined;
    const attempt = this.attempt();
    const correlationId =
      attempt?.correlationId ?? this.pendingCorrelationId ?? 'unknown';
    const maximizerUserUid = attempt?.maximizerUserUid ?? '';

    const carriedId = attempt?.userSyncConfigurationId;

    const telemetry: Record<string, string> = {
      feature: 'sync-configuration-install',
      step,
      maximizerUserUid,
      correlationId,
      errorMessage,
      timestampUtc: new Date().toISOString(),
      ...(httpStatus !== undefined ? { httpStatus: String(httpStatus) } : {}),
      ...(carriedId ? { userSyncConfigurationId: carriedId } : {}),
      ...extraProps,
    };

    const exception = error instanceof Error ? error : new Error(errorMessage);
    this.insights.trackException({ exception }, telemetry);

    const failure = {
      step,
      httpStatus,
      message: errorMessage,
      ...(dialogBodyKey ? { dialogBodyKey } : {}),
    };

    this.attempt.update((current) =>
      current
        ? { ...current, failure, step }
        : {
            correlationId,
            maximizerUserUid,
            maximizerUserEmail: '',
            workspaceId: '',
            step,
            failure,
          },
    );

    const cancelled = step === 'microsoft-oauth-denied-or-cancelled';
    this.trackSyncConfigurationEvent(
      'install',
      cancelled ? 'cancelled' : 'failed',
      {
        result: 'failed',
        step,
        ...(httpStatus !== undefined ? { httpStatus } : {}),
        errorMessage,
      },
    );
  }

  private trackSyncConfigurationEvent(
    journey: string,
    action: string,
    metadata: Record<string, unknown>,
  ): void {
    this.tracker.trackEvent({
      module: TRACKER_MODULE,
      journey,
      action,
      metadata,
    });
  }

  private classifyCallbackFailure(
    oauthErrorCode: string | null,
    oauthError: string | null,
  ): InstallStep {
    // Azure sends a stable code in `error`; `error_description` is free prose that must not drive classification.
    if (
      oauthErrorCode &&
      /^(access_denied|consent_required|login_required|interaction_required)$/i.test(
        oauthErrorCode.trim(),
      )
    ) {
      return 'microsoft-oauth-denied-or-cancelled';
    }
    if (!oauthError) return 'microsoft-oauth-callback';
    if (/state validation|csrf/i.test(oauthError)) {
      return 'microsoft-oauth-state-invalid';
    }
    if (
      /access[_\s-]?denied|consent[_\s-]?required|user[_\s-]?cancel|declined to consent|cancell?ed/i.test(
        oauthError,
      )
    ) {
      return 'microsoft-oauth-denied-or-cancelled';
    }
    return 'microsoft-oauth-callback';
  }

  private advance(step: InstallStep | 'success' | 'install-superseded'): void {
    this.attempt.update((current) =>
      current ? { ...current, step } : current,
    );
  }

  private failureResult(): { type: 'failure' } | { type: 'session-expired' } {
    this.signalOpenerComplete();
    return this.sessionExpired() !== null
      ? { type: 'session-expired' }
      : { type: 'failure' };
  }

  private signalOpenerComplete(): void {
    try {
      if (typeof window === 'undefined') return;
      window.opener?.postMessage(
        { type: HANDSHAKE_COMPLETE_MESSAGE_TYPE },
        window.location.origin,
      );
    } catch {
      // best-effort
    }
  }

  private describeError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return `${error.status} ${error.message} — ${this.errorBodyMessage(error)}`;
    }
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'Unknown install-flow error.';
  }

  private errorBodyMessage(error: HttpErrorResponse): string {
    if (typeof error.error === 'string') return error.error;
    if (
      error.error &&
      typeof error.error === 'object' &&
      'message' in error.error
    ) {
      return String((error.error as { message: unknown }).message);
    }
    return error.message;
  }

  /**
   * True when the update was rejected before the backend could commit the new credential:
   * the validation pipeline (duplicate account, foreign or unknown id) runs ahead of the handler.
   * A 5xx is excluded because the handler persists the PAT as the live credential before the
   * later steps that can throw, so revoking then would break a working configuration.
   */
  private isRejectedBeforeCommit(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      (error.status === HttpStatusCode.BadRequest ||
        error.status === HttpStatusCode.NotFound ||
        error.status === HttpStatusCode.UnprocessableEntity)
    );
  }

  /** Duplicate-account conflict is the one backend validation the user can act on themselves. */
  private hasMicrosoftEmailConflict(error: HttpErrorResponse): boolean {
    // createConfiguration uses responseType:'text' (raw string body); updateConfiguration parses JSON — handle both.
    let body: unknown = error.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        return false;
      }
    }
    if (!body || typeof body !== 'object') return false;
    const envelope =
      (body as { errors?: unknown }).errors ??
      (body as { error?: { errors?: unknown } }).error?.errors;
    if (!envelope || typeof envelope !== 'object') return false;
    const entry = (envelope as Record<string, unknown>)['MicrosoftUserEmail'];
    return (
      Array.isArray(entry) &&
      entry.some((value) => typeof value === 'string' && value.length > 0)
    );
  }

}
